



































































































import { Component, Vue, Prop, Emit } from "vue-property-decorator";
import { authGuard } from "../auth/authGuard";
import { Layout } from "../interfaces/layout";
import { WidgetConfiguration } from "../interfaces/widgetConfiguration";
import { WidgetState } from "../interfaces/widgetState";
import { WidgetPayload } from "../interfaces/widgetPayload";
import ScriptBuilderBar from "@/components/ScriptBuilderBar.vue";
import randomstring from "randomstring";
import ScriptBuilderPage from "@/components/ScriptBuilderPage.vue";
import { StudioAttributes } from "../interfaces/studioAttributes";
import ModifyScriptCard from "@/components/ModifyScriptCard.vue";
import ScriptBuilderLanding from "@/components/ScriptBuilderLanding.vue";
import { User } from "../interfaces/user";
import { ScriptService } from "../services/scriptService";
import { Script } from "../interfaces/script";
import { Report } from "../interfaces/report";
import { WidgetService } from "../services/widgetService";
import { FileService } from "../services/fileService";
import { Page } from "../interfaces/page";
import BackgroundSettings from "@/interfaces/studioBackground";

@Component({
    components: {
        ScriptBuilderBar,
        ScriptBuilderPage,
        ModifyScriptCard,
        ScriptBuilderLanding,
    },
    beforeRouteEnter: authGuard,
})
export default class ScriptBuilder extends Vue {
    @Prop() providedReport?: Report;

    public currentPage: number = 0;
    public get maxPage(): number {
        return this.pages.length - 1;
    }

    public loading = false;
    public saveScriptDialog = false;
    public loadScriptDialog = false;
    public importing = false;

    public deletePageDialog = false;
    public deletePageIndex: number = -1;

    public pages: {
        attributes: StudioAttributes;
        layout: Layout[];
        configurationIds: string[];
        backgroundSettings: BackgroundSettings | null;
        title: string;
    }[] = [];

    public get user(): User | null {
        return this.$store.state.user;
    }

    public mounted() {
        if (this.providedReport) {
            this.loadFromReport(this.providedReport);
        }
    }

    public loadFromReport(report: Report) {
        const configurations = report.reportData
            .map(c => c.widgetConfig)
            .reduce((prev, curr) => [...prev, ...curr], [])
            .filter(
                (c, i, arr) =>
                    arr.findIndex(x => x.widgetId == c.widgetId) === i
            );

        const pages: {
            attributes: StudioAttributes;
            layout: Layout[];
            configurationIds: string[];
            backgroundSettings: BackgroundSettings | null;
            title: string;
        }[] = report.reportData.map(p => ({
            attributes: p.attributes,
            layout: p.layout,
            configurationIds: p.widgetConfig.map(c => c.widgetId),
            backgroundSettings: p.backgroundSettings,
            title: p.title,
        }));

        this.configurations = configurations;
        this.pages = pages;
        this.currentPage = 0;
    }

    @Emit() leave() {
        this.currentPage = -1;
        this.configurations = [];
        this.pages = [];
        if (this.providedReport) {
            return true;
        }
    }

    public newPage() {
        this.pages.push({
            attributes: { fullscreenWidgetId: null },
            layout: [],
            configurationIds: [],
            backgroundSettings: null,
            title: "",
        });

        if (this.currentPage < 0) {
            this.currentPage = 0;
        }
    }

    public setPage(event: { index: number }) {
        this.currentPage = event.index;
    }

    public configurations: WidgetConfiguration<
        WidgetState,
        WidgetPayload
    >[] = [];

    public async toolboxClick(event: { type: string; static: boolean }) {
        const token = await this.$auth?.getTokenSilently();
        if (!token) {
            throw new Error("Not Authorized");
        }
        const state = await WidgetService.getDefaultState(event.type, token);
        const widgetId = event.static
            ? event.type
            : randomstring.generate({
                  length: 12,
              });
        const config: WidgetConfiguration<WidgetState, WidgetPayload> = {
            widgetId,
            type: event.type,
            state,
        };
        this.widgetAdd(this.currentPage, config, {
            x: 0,
            y: 0,
            w: 5,
            h: 8,
        });
    }

    public async updateState(event: {
        config: WidgetConfiguration<WidgetState, WidgetPayload>;
        payload: WidgetPayload;
    }) {
        const token = await this.$auth?.getTokenSilently();
        if (!token) {
            throw new Error("Not Authorized");
        }
        const config = this.configurations.find(
            c => c.widgetId === event.config.widgetId
        );
        if (config) {
            config.state = await WidgetService.parsePayload(
                event.config,
                event.payload,
                token
            );
        }
    }

    public toolboxReuse(event: { widgetId: string }) {
        this.widgetAssign(this.currentPage, event.widgetId, {
            x: 0,
            y: 0,
            w: 5,
            h: 10,
            i: event.widgetId,
        });
    }

    public widgetAssign(page: number, widgetId: string, layout: Layout) {
        if (layout.i === "jitsi") {
            this.pages[page].layout.unshift(layout);
        } else {
            this.pages[page].layout.push(layout);
        }
        this.pages[page].configurationIds.push(widgetId);
    }

    public widgetAdd(
        page: number,
        widget: WidgetConfiguration<WidgetState, WidgetPayload>,
        layout: Omit<Layout, "i">
    ) {
        if (widget.widgetId === "jitsi") {
            this.pages[page].layout.unshift({ ...layout, i: widget.widgetId });
        } else {
            this.pages[page].layout.push({ ...layout, i: widget.widgetId });
        }
        this.pages[page].configurationIds.push(widget.widgetId);
        this.configurations.push(widget);
    }

    public widgetRemove(page: number, widgetId: string) {
        const index = this.pages[page].configurationIds.indexOf(widgetId);
        if (index >= 0) {
            this.pages[page].configurationIds.splice(index, 1);
        }
        const layoutIndex = this.pages[page].layout.findIndex(
            l => l.i === widgetId
        );
        if (layoutIndex >= 0) {
            this.pages[page].layout.splice(layoutIndex, 1);
        }

        if (
            !this.pages
                .map(p => p.configurationIds)
                .some(ids => ids.includes(widgetId))
        ) {
            const configIndex = this.configurations.findIndex(
                c => c.widgetId === widgetId
            );
            if (configIndex >= 0) {
                this.configurations.splice(configIndex, 1);
            }
        }
    }

    public removePageStart(event: { index: number }) {
        this.deletePageIndex = event.index;
        this.deletePageDialog = true;
    }

    public removePageCancel() {
        this.deletePageDialog = false;
        this.deletePageIndex = -1;
    }

    public removePage() {
        if (this.deletePageIndex >= 0) {
            const configurationIds = [
                ...this.pages[this.deletePageIndex].configurationIds,
            ];
            configurationIds.forEach(widgetId =>
                this.widgetRemove(this.deletePageIndex, widgetId)
            );
            this.pages.splice(this.deletePageIndex, 1);
        }

        if (this.deletePageIndex <= this.currentPage && this.currentPage > 0) {
            this.currentPage -= 1;
        }

        this.deletePageDialog = false;
        this.deletePageIndex = -1;
    }

    public onFullscreen(
        page: number,
        event: { widgetId: string; fullscreen: boolean }
    ) {
        if (event.fullscreen) {
            this.pages[page].attributes.fullscreenWidgetId = event.widgetId;
        } else {
            this.pages[page].attributes.fullscreenWidgetId = null;
        }
    }

    public startSave() {
        this.saveScriptDialog = true;
    }

    public startLoad() {
        this.loadScriptDialog = true;
    }

    public async load(script: { id: string; name: string }) {
        this.loading = true;
        try {
            const user = this.user;
            if (!user) {
                return;
            }

            const token = await this.$auth.getTokenSilently();

            const fullScript: Script = await ScriptService.getScriptById(
                script.id,
                token
            );
            fullScript.pages.sort(
                (pageA: Page, pageB: Page) => pageA.id - pageB.id
            );
            const configurations = fullScript.pages
                .map(c => c.widgetConfig)
                .reduce((prev, curr) => [...prev, ...curr], [])
                .filter(
                    (c, i, arr) =>
                        arr.findIndex(x => x.widgetId == c.widgetId) === i
                );

            const pages: {
                attributes: StudioAttributes;
                layout: Layout[];
                configurationIds: string[];
                backgroundSettings: BackgroundSettings | null;
                title: string;
            }[] = fullScript.pages.map(p => ({
                attributes: p.attributes,
                layout: p.layout,
                configurationIds: p.widgetConfig.map(c => c.widgetId),
                backgroundSettings: p.backgroundSettings,
                title: p.title,
            }));

            this.configurations = configurations;
            this.pages = pages;
        } finally {
            this.loadScriptDialog = false;
            this.currentPage = 0;
            this.loading = false;
        }
    }

    public async deleteScript(id: string) {
        if (id) {
            const token = await this.$auth.getTokenSilently();
            await ScriptService.deleteScript(id, token);
            this.$store.commit("removeUserScript", id);
        }
    }

    public async save(name: string) {
        this.loading = true;
        let cleanupNeeded: boolean = false;
        try {
            const user = this.user;
            if (!user) {
                return;
            }

            const scripts = user.scripts;
            const token = await this.$auth.getTokenSilently();
            if (scripts) {
                let script = scripts.find(s => s.name == name.trim());

                if (!script) {
                    script = await ScriptService.addScript(
                        name,
                        user.id,
                        token
                    );
                    cleanupNeeded = true;
                }

                if (script) {
                    const scriptId = script.id;
                    const mappedPages: Page[] = this.pages.map((p, i) => ({
                        id: i,
                        // eslint-disable-next-line @typescript-eslint/camelcase
                        script_id: scriptId,
                        layout: p.layout,
                        attributes: p.attributes,
                        widgetConfig: p.configurationIds
                            .map(cId =>
                                this.configurations.find(
                                    c => c.widgetId === cId
                                )
                            )
                            .filter(c => !!c) as WidgetConfiguration<
                            WidgetState,
                            WidgetPayload
                        >[],
                        backgroundSettings: p.backgroundSettings,
                        title: p.title,
                    }));

                    script.pages = mappedPages;
                    const result = await ScriptService.updateScript(
                        script,
                        token
                    );
                    if (result.status == 200) {
                        this.$store.commit("addUserScript", {
                            script,
                        });
                    } else {
                        this.$store.commit("showSnackbar", {
                            text: result.data.detail,
                            color: "error",
                        });
                        if (cleanupNeeded) {
                            ScriptService.deleteScript(script.id, token);
                        }
                    }
                }
            }
        } finally {
            this.saveScriptDialog = false;
            this.loading = false;
        }
    }

    public async startConvertPptx(event: { file: File }) {
        this.importing = true;
        try {
            const token = await this.$auth.getTokenSilently();

            const response = await FileService.startConvertPptx(
                event.file,
                token
            );
            if (response.status == 404) {
                this.$store.commit("showSnackbar", {
                    text: this.$t("script.differentName"),
                    color: "error",
                });
                this.importing = false;
                return;
            } else if (response.status == 413) {
                this.$store.commit("showSnackbar", {
                    text: this.$t("script.tooLarge"),
                    color: "error",
                });
                this.importing = false;
                return;
            }
            if (response.status != 200) {
                this.$store.commit("showSnackbar", {
                    color: "error",
                    text: response.data,
                });
                this.importing = false;
                return;
            }
            this.$store.commit("showSnackbar", {
                text: "Conversion started",
                color: "success",
            });
            const handle = setInterval(() => {
                FileService.checkConversion(response.data, token)
                    .then(async status => {
                        if ("scriptId" in status) {
                            this.$store.commit("showSnackbar", {
                                text: "Fertig convertiert",
                                color: "success",
                            });
                            clearInterval(handle);
                            this.importing = false;
                            // add to store
                            const fullScript: Script = await ScriptService.getScriptById(
                                status.scriptId,
                                token
                            );
                            this.$store.commit("addUserScript", {
                                script: fullScript,
                            });
                            this.load({
                                id: fullScript.id,
                                name: fullScript.name,
                            });
                        } else if ("percentage" in status) {
                            this.$store.commit("showSnackbar", {
                                text: status.percentage + "% done",
                                color: "warning",
                            });
                        } else if ("pending" in status) {
                            this.$store.commit("showSnackbar", {
                                text: status.pending,
                                color: "warning",
                            });
                        } else if ("err" in status) {
                            this.$store.commit("showSnackbar", {
                                text: status,
                                color: "error",
                            });
                            clearInterval(handle);
                            this.importing = false;
                        }
                    })
                    .catch(err => {
                        console.warn(err);
                        console.warn("Error converting pptx");
                        clearInterval(handle);
                    });
            }, 2000);
        } catch {
            this.importing = false;
        }
    }
    public get cssProps() {
        const page: {
            attributes: StudioAttributes;
            layout: Layout[];
            configurationIds: string[];
            backgroundSettings: BackgroundSettings | null;
        } = this.pages[this.currentPage];
        if (page.backgroundSettings) {
            return {
                backgroundImage: `url(/api/file/by_id/${page.backgroundSettings.image})`,
                backgroundRepeat: "no-repeat",
                backgroundSize: page.backgroundSettings.size,
                backgroundPosition: page.backgroundSettings.position,
            };
        } else return {};
    }

    public reset() {
        this.currentPage = 0;
    }
}
